import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Typography,
  Chip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import PaperWrapper from "../../../../../components/PaperWrapper";

export const FolderFilters = ({
  searchTerm,
  onSearchChange,
  onClearSearch,
  selectedPath,
  onPathFilter,
  uniquePaths,
  onRefresh,
  loading,
  filteredFolders,
  totalFolders,
  lastSyncTime,
  sortBy,
  sortOrder,
  onSortChange,
  onRunSync,
  syncing,
}: any) => {
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      <PaperWrapper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: "background.paper",
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Search Field and Action Buttons */}
            {lastSyncTime && (
              <Typography variant="body2" sx={{}}>
                Last sync time: {lastSyncTime || "N/A"}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                fullWidth
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "4.5px 14px",
                  },
                }}
                placeholder="Search folders by name or path..."
                value={searchTerm}
                onChange={onSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={onClearSearch}
                        sx={{ minWidth: "auto", p: 0.5 }}
                      >
                        <ClearIcon fontSize="small" />
                      </Button>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 0 },
                }}
                size="small"
              />

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="sort-select-label">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Sort
                  </Box>
                </InputLabel>
                <Select
                  labelId="sort-select-label"
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split("-");
                    onSortChange(field, order);
                  }}
                  label="Sort"
                  sx={{ borderRadius: 0, height: "32px" }}
                >
                  <MenuItem value="name-asc">Name A-Z</MenuItem>
                  <MenuItem value="name-desc">Name Z-A</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                disabled={loading}
                size="small"
                sx={{
                  borderRadius: 0,
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  height: "32px",
                }}
              >
                {loading ? "Loading..." : "Refresh"}
              </Button>
              {onRunSync && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SyncIcon />}
                  onClick={onRunSync}
                  disabled={syncing || loading}
                  size="small"
                  sx={{
                    borderRadius: 0,
                    minWidth: "auto",
                    whiteSpace: "nowrap",
                    height: "32px",
                  }}
                >
                  {syncing ? "Starting..." : "Run Dropbox Sync"}
                </Button>
              )}
            </Box>

            {/* Path Filter Chips */}
            {uniquePaths.length > 0 && (
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Filter by path:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {uniquePaths.map((path: any) => (
                    <Chip
                      key={path}
                      label={path}
                      onClick={() => onPathFilter(path)}
                      color={selectedPath === path ? "primary" : "default"}
                      variant={selectedPath === path ? "filled" : "outlined"}
                      size="small"
                      sx={{ borderRadius: 0 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Active Filters */}
            {(searchTerm ||
              selectedPath ||
              sortBy !== "name" ||
              sortOrder !== "asc") && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Active filters:
                  </Typography>
                  {searchTerm && (
                    <Chip
                      label={`Search: "${searchTerm}"`}
                      onDelete={onClearSearch}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {selectedPath && (
                    <Chip
                      label={`Path: "${selectedPath}"`}
                      onDelete={() => onPathFilter(selectedPath)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {(sortBy !== "name" || sortOrder !== "asc") && (
                    <Chip
                      label={`Sort: ${sortBy === "name" ? (sortOrder === "asc" ? "Name A-Z" : "Name Z-A") : `${sortBy} ${sortOrder}`}`}
                      onDelete={() => onSortChange("name", "asc")}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {filteredFolders.length !== totalFolders && (
                    <Chip
                      label={`${filteredFolders.length} of ${totalFolders}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </PaperWrapper>
    </Box>
  );
};

export default FolderFilters;
